/**
 * ArchiveSessionDialog - Confirmation dialog for archiving a session
 *
 * Archived sessions are hidden from the list but kept for profile generation.
 */

import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useArchiveSession } from '@/hooks/useSessions'

interface ArchiveSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  sessionTitle: string
}

export function ArchiveSessionDialog({
  open,
  onOpenChange,
  sessionId,
  sessionTitle,
}: ArchiveSessionDialogProps) {
  const { t } = useTranslation()
  const archiveSession = useArchiveSession()

  const handleArchive = async () => {
    try {
      await archiveSession.mutateAsync({ id: sessionId, title: sessionTitle })
      onOpenChange(false)
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-amber-600">
            {t('session.archiveTitle')}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-neutral-900 block mb-2">
              "{sessionTitle || t('session.newConversation')}"
            </span>
            {t('session.archiveConfirm')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleArchive}
            disabled={archiveSession.isPending}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {archiveSession.isPending ? (
              <Spinner className="h-4 w-4 mr-2" />
            ) : null}
            {t('session.archive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
